<template>
  <div>
    <!-- Side Menu -->
    <div
      class="side-menu"
      :class="{ open: isMenuOpen }"
    >
      <ul>
        <li class="menu-header">
          <i class="fas fa-home" />
          <RouterLink to="/">
            Utama
          </RouterLink>
        </li>
        <li
          v-for="item in filteredMenuItems"
          :key="item.title"
        >
          <RouterLink :to="item.route">
            {{ item.title }}
          </RouterLink>
        </li>
      </ul>
    </div>
    <div
      v-if="isMenuOpen"
      class="overlay"
      @click="closeMenus"
    />
    <button
      v-if="!isMenuOpen"
      class="menu-toggle-button"
      @click="toggleMenu"
    >
      <i class="fas fa-home" />
    </button>
  </div>
</template>


<script setup>
import { useAuthStore } from '@/store/auth'
import { computed, ref } from 'vue'

const isMenuOpen = ref(false)

const authStore = useAuthStore()
const roles = authStore.getRoles()

const menuItems = [
  { title: 'Inspektorat', route: '/inspektorat' },
  { title: 'Pembangunan', route: '/pembangunan' },
  { title: 'Kesihatan Persekitaran', route: '/kesihatan-persekitaran' },
  { title: 'Kejuruteraan Kesihatan Umum', route: '/kejuruteraan-kesihatan-umum' },
  { title: 'Kejuruteraan Awam', route: '/kejuruteraan-awam' },
  { title: 'Kawalan Pemajuan', route: '/kawalan-pemajuan' },
  { title: 'Kampung Baru', route: '/kampung-baru' },
  { title: 'Khidmat Pengurusan', route: '/khidmat-pengurusan' },
  { title: 'Pengurusan Maklumat', route: '/pengurusan-maklumat' },
]

// Computed filtered menu items
const filteredMenuItems = computed(() => {
  // If the user is a manager, show all items
  if (roles.includes('manager')) {
    return menuItems
  }

  // Filter items based on roles
  return menuItems.filter(item =>
    roles.some(role => role.toLowerCase() === item.title.toLowerCase()),
  )
})

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
  if (isMenuOpen.value) {
    closeAllMenus()
  }
}

const closeMenus = () => {
  isMenuOpen.value = false
  closeAllMenus()
}

// Define individual close functions for each menu
const closeInspektoratMenu = () => {
  window.dispatchEvent(new CustomEvent('close-inspektorat-menu'))
}

const closeKampungBaruMenu = () => {
  window.dispatchEvent(new CustomEvent('close-kampung-baru-menu'))
}

const closeKawalanPemajuanMenu = () => {
  window.dispatchEvent(new CustomEvent('close-kawalan-pemajuan-menu'))
}

const closeKejuruteraanAwamMenu = () => {
  window.dispatchEvent(new CustomEvent('close-kejuruteraan-awam-menu'))
}

const closeKejuruteraanKesihatanMenu = () => {
  window.dispatchEvent(new CustomEvent('close-kejuruteraan-kesihatan-menu'))
}

const closeKesihatanPersekitaranMenu = () => {
  window.dispatchEvent(new CustomEvent('close-kesihatan-persekitaran-menu'))
}

const closeKhidmatPengurusanMenu = () => {
  window.dispatchEvent(new CustomEvent('close-khidmat-pengurusan-menu'))
}

const closePembangunanMenu = () => {
  window.dispatchEvent(new CustomEvent('close-pembangunan-menu'))
}

const closePengurusanMaklumatMenu = () => {
  window.dispatchEvent(new CustomEvent('close-pengurusan-maklumat-menu'))
}

// Close all menus at once
const closeAllMenus = () => {
  closeInspektoratMenu()
  closeKampungBaruMenu()
  closeKawalanPemajuanMenu()
  closeKejuruteraanAwamMenu()
  closeKejuruteraanKesihatanMenu()
  closeKesihatanPersekitaranMenu()
  closeKhidmatPengurusanMenu()
  closePembangunanMenu()
  closePengurusanMaklumatMenu()
}

window.addEventListener('close-main-menu', () => {
  isMenuOpen.value = false
})
</script>


<style scoped>
/* Side Menu */
.side-menu {
  position: fixed;
  z-index: 1000;
  border-radius: 10px;
  background-color: #312867;
  block-size: auto;
  box-shadow: 0 0 10px rgba(0, 0, 0, 30%);
  color: white;
  inline-size: 210px;
  inset-block-start: 20%;
  inset-inline-end: 0;
  max-block-size: 70%;
  padding-block: 10px;
  padding-inline: 15px;
  transform: translateX(100%);
  transition: transform 0.3s;
}

.side-menu.open {
  transform: translateX(0);
}

.side-menu ul {
  padding: 0;
  margin: 0;
  list-style: none;
}

.side-menu li {
  margin-block-end: 10px;
}

.side-menu a,
.side-menu router-link {
  color: white;
  text-decoration: none;
}

.overlay {
  position: fixed;
  z-index: 999;
  background: rgba(0, 0, 0, 50%);
  block-size: 100%;
  inline-size: 100%;
  inset-block-start: 0;
  inset-inline-start: 0;
}

/* Menu Toggle Button */
.menu-toggle-button {
  position: fixed;
  z-index: 1001;
  padding: 10px;
  border: none;
  border-radius: 5px 0 0 5px;
  background-color: #312867;
  color: white;
  cursor: pointer;
  font-size: 1.5rem;
  inset-block-start: 20%;
  inset-inline-end: 0;
  transform: translateY(-50%);
  transition: inset-inline-end 0.3s;
}

.menu-toggle-button i {
  font-size: 1.5rem;
}

.menu-toggle-button:hover {
  inset-inline-end: 5px;
}

.menu-header {
  display: flex;
  align-items: center;
  font-size: 1.2rem;
  font-weight: bold;
  gap: 10px;
}

/* 1024px */
@media (max-width: 1024px) {
  .side-menu {
    inline-size: 180px;
    inset-block-start: 15%;
    max-block-size: 80%;
  }
  
  .menu-toggle-button {
    inset-block-start: 15%;
    padding: 8px;
  }

  .menu-header {
    font-size: 1.1rem;
  }
}

/* 768px */
@media (max-width: 768px) {
  .side-menu {
    inline-size: 160px;
    inset-block-start: 15%;
    max-block-size: 90%;
  }

  .menu-toggle-button {
    inset-block-start: 15%;
    font-size: 1.3rem;
  }

  .menu-header {
    font-size: 1rem;
  }

  .side-menu li {
    font-size: 0.9rem;
  }
}

/* 480px */
@media (max-width: 480px) {
  .side-menu {
    inline-size: 140px;
    inset-block-start: 2%;
    max-block-size: 95%;
    padding-inline: 10px;
  }

  .menu-toggle-button {
    inset-block-start: 2%;
    font-size: 1.2rem;
  }

  .menu-header {
    font-size: 0.9rem;
  }

  .side-menu li {
    font-size: 0.8rem;
  }
}
</style>
